import { OAuthServiceInterface, OAuthServiceResponse } from "../interfaces";
import { UserServiceContext } from "../providers";

export type OAuthServiceProps = {
    client_id: string
    redirect_uri: string
    scope: string
    url: string
    key: string
    extra_params?: Record<string, string>
    method?: 'GET' | 'POST'
}

// This is just used to generate a nonce for Apple signin
const uuid = () => 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, replaced => {
    const num = Math.random() * 16 | 0
    const char = replaced == 'x' ? num : (num & 0x3 | 0x8)
    return char.toString(16)
})

// This happens once, no matter how many implementations of OAuth we have, because we
//   only need to check for an OAuth response one time. Then all of the services we create
//   can just check this promise to see if they've gotten a response.
const resolution = Promise.resolve(
    // I'm being too clever here, maybe.
    // The next line turns const params into an object representing the hash options in the URL
    // http://url#token=A&state=B results in {token: A, state: B}
    Object.assign.apply(null,
        [{}, ...([] as string[])
            .concat(window.location.search.substring(1).split('&'))
            .concat(window.location.hash.substring(1).split('&'))
            .map(param => {
                const keyVal = param.split('=')
                if (keyVal.length)
                    return {
                        [keyVal[0]]: keyVal[1]
                    }
                return null
        })
        .filter(param => param !== null)]
    )
)

export class OAuthService implements OAuthServiceInterface {
    private client_id: string;
    private key: string;
    private redirect_uri: string;
    private scope: string;
    private url: string;
    private method: string;
    private extra_params: Record<string, string> | null;
    protected _when: Promise<OAuthServiceResponse>;

    constructor({client_id, redirect_uri, scope, url, extra_params, key, method = 'POST'}: OAuthServiceProps) {
        this.client_id = client_id
        this.redirect_uri = redirect_uri
        this.scope = scope
        this.extra_params = extra_params ? extra_params : null
        this.key = key
        this.url = url
        this.method = method

        this._when = resolution.then(response => new Promise(resolve => {
            if (response === false || response.state !== this.key)
                return // Don't call resolve or reject, we just never notify anyone
            resolve(response)
        }))
    }

    get when() {
        return this._when
    }

    login(): void {
        const params: Record<string, string> = {
            client_id: this.client_id,
            redirect_uri: this.redirect_uri,
            scope: this.scope,
            state: this.key,
            ...this.extra_params
        }

        let form = document.createElement('form')
        form.setAttribute('method', this.method)
        form.setAttribute('action', this.url)
        for (var param in params) {
            let input = document.createElement('input')
            input.setAttribute('type', 'hidden')
            input.setAttribute('name', param)
            input.setAttribute('value', params[param])
            form.appendChild(input)
        }
    
        document.body.appendChild(form)
        form.submit()
    }

    protected checkResolution() { 
        return resolution 
    }
}

export class AppleOAuthService extends OAuthService {
    constructor({extra_params, ...config}: Partial<OAuthServiceProps> & Pick<OAuthServiceProps, 'client_id' | 'redirect_uri'>) {
        super({
            scope: 'email name',
            key: 'apple', 
            url: 'https://appleid.apple.com/auth/authorize',
            extra_params: {
                response_mode: 'form_post',
                response_type: 'id_token code',
                nonce: uuid(),
                ...extra_params
            },
            method: 'GET',
            ...config
        })

        // Apple is a bit different so we need to adjust, sending code instead of access_token
        this._when = this.when.then(response => new Promise(resolve => {
            if (response.id_token) {
                // For the purposes of communicating with Cheetah, the code is the access token
                response.access_token = response.id_token
                resolve(response)
            }
            // No code? Don't even resolve
        }))
    }
}

export class FacebookOAuthService extends OAuthService {
    constructor({ extra_params, ...config }: Partial<OAuthServiceProps> & Pick<OAuthServiceProps, 'client_id' | 'redirect_uri'>) {
        super({
            scope: 'email,public_profile',
            key: 'facebook', 
            url: 'https://www.facebook.com/v11.0/dialog/oauth',
            extra_params: {
                response_type: 'token'
            },
            method: 'GET',
            ...config
        })
    }
}

export class GoogleOAuthService extends OAuthService {
    constructor({ extra_params = {}, ...config }: Partial<OAuthServiceProps> & Pick<OAuthServiceProps, 'client_id' | 'redirect_uri'>) {
        super({
            scope: 'https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile',
            key: 'google', 
            url: 'https://accounts.google.com/o/oauth2/v2/auth',
            extra_params: {
                response_type: 'token',
                include_granted_scopes: 'true',
                ...extra_params
            },
            ...config
        })
    }
}